import { SearchBoxCore, SessionToken } from "@mapbox/search-js-core";
import type { SearchBoxAdministrativeUnitTypes } from "@mapbox/search-js-core/dist/searchbox/types";
import type { MyBLINK } from "../types/generated";

interface MapboxSuggestion {
    mapbox_id: string;
    name: string;
    full_address?: string;
    place_formatted?: string;
    address?: MyBLINK.RefundAddress;
}

interface MapboxAddressSearchConfig {
    wire: {
        set(property: string, value: string | null, live?: boolean): void;
    };
    accessToken: string;
    wireModel: string;
    wireMapboxId?: string;
    initialValue: string;
    initialDisplayValue?: string;
    refundAddress?: boolean;
}

interface MapboxAddressFeature {
    properties: {
        feature_type?: string;
        mapbox_id?: string;
        context?: {
            address?: { street_name?: string; address_number?: string };
            postcode?: { name?: string };
            place?: { name?: string };
            country?: { country_code?: string };
        };
    };
}

export default function mapboxAddressSearch(config: MapboxAddressSearchConfig) {
    let searchBox: SearchBoxCore | null = null;
    let sessionToken: SessionToken | null = null;
    let requestVersion = 0;
    let controller: AbortController | null = null;

    return {
        accessToken: config.accessToken,
        wireModel: config.wireModel,
        wireMapboxId: config.wireMapboxId,
        displayValue: config.initialValue || "",
        suggestions: [] as MapboxSuggestion[],
        showSuggestions: false,
        highlightedIndex: -1,
        isLoading: false,
        searchFailed: false,
        hasSelection: !!config.initialValue,

        init() {
            if (config.refundAddress) {
                this.displayValue = config.initialDisplayValue || '';
                if (config.initialValue) {
                    const address: MyBLINK.RefundAddress = JSON.parse(
                        config.initialValue,
                    );
                    this.displayValue = `${address.street}, ${address.zip} ${address.place}`;
                }
                return;
            }

            searchBox = new SearchBoxCore({
                accessToken: this.accessToken,
            });
            sessionToken = new SessionToken();
        },

        handleInput(query: string) {
            requestVersion++;
            this.suggestions = [];
            this.highlightedIndex = -1;
            this.isLoading = false;

            if (this.wireMapboxId) {
                config.wire.set(this.wireMapboxId, null, false);
                config.wire.set(this.wireModel, query.trim() ? query : null, false);
            }

            if (!query.trim()) {
                this.showSuggestions = false;

                if (this.wireModel) {
                    config.wire.set(this.wireModel, null, false);
                }
            }
        },

        async search(query: string) {
            if (query.trim().length < 2 || query !== this.displayValue) {
                return;
            }

            if (config.refundAddress) {
                return this.searchRefundAddresses(query);
            }

            if (!searchBox || !sessionToken) {
                return;
            }

            const version = ++requestVersion;
            this.isLoading = true;

            try {
                const response = await searchBox.suggest(query, {
                    sessionToken: sessionToken,
                    limit: 5,
                    language: "de",
                    country: "CH",
                    types: [
                        "address",
                        "street",
                        "poi",
                    ] as unknown as Set<SearchBoxAdministrativeUnitTypes>,
                });

                if (version !== requestVersion) {
                    return;
                }

                this.suggestions = response.suggestions || [];
                this.showSuggestions = true;
                this.highlightedIndex = -1;
            } catch (error) {
                if (version === requestVersion) {
                    console.error("Mapbox search error:", error);
                    this.suggestions = [];
                }
            } finally {
                if (version === requestVersion) {
                    this.isLoading = false;
                }
            }
        },

        async selectSuggestion(suggestion: MapboxSuggestion) {
            if (config.refundAddress && suggestion.address) {
                controller?.abort();
                this.updateRefundAddress(JSON.stringify(suggestion.address));
                this.displayValue = suggestion.full_address || suggestion.name;
                this.hasSelection = true;
                this.suggestions = [];
                this.showSuggestions = false;
                this.isLoading = false;
                return;
            }

            if (!searchBox || !sessionToken) {
                return;
            }

            const version = ++requestVersion;
            this.isLoading = true;

            try {
                const result = await searchBox.retrieve(suggestion, {
                    sessionToken: sessionToken,
                    language: "de",
                    country: "CH",
                });

                if (version !== requestVersion) {
                    return;
                }

                const feature = result.features?.[0];
                if (feature) {
                    const fullAddress =
                        feature.properties.full_address ||
                        feature.properties.name;
                    const mapboxId = feature.properties.mapbox_id;

                    this.displayValue = fullAddress;

                    if (this.wireModel) {
                        config.wire.set(this.wireModel, fullAddress);
                    }
                    if (this.wireMapboxId) {
                        config.wire.set(this.wireMapboxId, mapboxId);
                    }
                }

                sessionToken = new SessionToken();
            } catch (error) {
                if (version === requestVersion) {
                    console.error("Mapbox retrieve error:", error);
                }
            } finally {
                if (version === requestVersion) {
                    this.suggestions = [];
                    this.showSuggestions = false;
                    this.isLoading = false;
                }
            }
        },

        clearRefundSelection() {
            controller?.abort();
            this.updateRefundAddress(null);
            this.hasSelection = false;
            this.suggestions = [];
            this.highlightedIndex = -1;
            this.isLoading = false;
            this.searchFailed = false;
            this.showSuggestions = true;
        },

        updateRefundAddress(value: string | null) {
            (
                this as unknown as {
                    $wire: {
                        set: (
                            path: string,
                            value: string | null,
                            live: boolean,
                        ) => void;
                    };
                }
            ).$wire.set(this.wireModel, value, false);
        },

        async searchRefundAddresses(query: string) {
            controller?.abort();
            if (query.trim().length < 3 || this.hasSelection) {
                this.suggestions = [];
                return;
            }

            controller = new AbortController();
            this.isLoading = true;
            this.searchFailed = false;

            try {
                const response = await fetch(
                    `https://api.mapbox.com/search/geocode/v6/forward?${new URLSearchParams(
                        {
                            q: query,
                            access_token: this.accessToken,
                            country: "ch",
                            language: "de",
                            types: "address",
                            permanent: "true",
                            limit: "5",
                        },
                    )}`,
                    { signal: controller.signal },
                );
                if (!response.ok) {
                    throw new Error("Address search failed");
                }

                const result: { features: MapboxAddressFeature[] } =
                    await response.json();
                if (this.displayValue !== query || this.hasSelection) {
                    return;
                }

                this.suggestions = result.features.flatMap(({ properties }) => {
                    const context = properties.context;
                    if (
                        properties.feature_type !== "address" ||
                        !properties.mapbox_id ||
                        !context?.address?.street_name ||
                        !context.address.address_number ||
                        !context.postcode?.name ||
                        !/^\d{4}$/.test(context.postcode.name) ||
                        !context.place?.name ||
                        context.country?.country_code?.toUpperCase() !== "CH"
                    ) {
                        return [];
                    }

                    return [
                        {
                            mapbox_id: properties.mapbox_id,
                            name: `${context.address.street_name} ${context.address.address_number}`,
                            full_address: `${context.address.street_name} ${context.address.address_number}, ${context.postcode.name} ${context.place.name}`,
                            address: {
                                street: `${context.address.street_name} ${context.address.address_number}`,
                                zip: context.postcode.name,
                                place: context.place.name,
                                country: "CH",
                                mapboxId: properties.mapbox_id,
                            },
                        },
                    ];
                });
                this.highlightedIndex = -1;
            } catch (error) {
                if (
                    !(
                        error instanceof DOMException &&
                        error.name === "AbortError"
                    ) &&
                    this.displayValue === query &&
                    !this.hasSelection
                ) {
                    this.searchFailed = true;
                    this.suggestions = [];
                }
            } finally {
                if (this.displayValue === query && !this.hasSelection) {
                    this.isLoading = false;
                }
            }
        },

        destroy() {
            controller?.abort();
        },

        handleBlur() {
            setTimeout(() => {
                this.showSuggestions = false;
            }, 200);
        },

        navigateDown() {
            if (this.highlightedIndex < this.suggestions.length - 1) {
                this.highlightedIndex++;
            }
        },

        navigateUp() {
            if (this.highlightedIndex > 0) {
                this.highlightedIndex--;
            }
        },

        selectHighlighted() {
            if (
                this.highlightedIndex >= 0 &&
                this.suggestions[this.highlightedIndex]
            ) {
                this.selectSuggestion(this.suggestions[this.highlightedIndex]);
            }
        },
    };
}
